import { Box, Skeleton, Text } from '@dixa/lib-shared-habla';
import { countries } from 'country-data-list';
import { useMemo } from 'react';

export type BillingInformationProps = {
  email?: string;
  companyName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  country?: string;
  name?: string;
  state?: string;
  postalCode?: string;
  vatin?: string;
  isLoading?: boolean;
};

export const BillingInformation = (
  props: BillingInformationProps,
): JSX.Element => {
  const { isLoading, ...billingInformation } = props;

  const {
    email,
    companyName,
    address1,
    address2,
    city,
    country,
    name,
    state,
    postalCode,
    vatin,
  } = billingInformation;

  const lineName = name;
  const lineContact = email;
  const lineAddress = [address1, address2].filter((val) => !!val).join(', ');
  const lineLocation = [city, postalCode].filter((val) => !!val).join(', ');

  const countryName = country ? countries[country]?.name : '';
  const lineCountry = [countryName, state].filter((val) => !!val).join(', ');
  const lineCompanyVatin = [companyName, vatin]
    .filter((val) => !!val)
    .join(', ');

  const isEmpty = useMemo(() => {
    return Object.values(billingInformation).every((val) => val === '');
  }, [billingInformation]);

  if (isLoading) {
    return (
      <Box>
        <Skeleton
          height="15px"
          width="100%"
          maxWidth="400px"
          margin="0 0 100 0"
        />
        <Skeleton
          height="15px"
          width="100%"
          maxWidth="400px"
          margin="0 0 300 0"
        />
        <Skeleton
          height="15px"
          width="100%"
          maxWidth="240px"
          margin="0 0 100 0"
        />
        <Skeleton
          height="15px"
          width="100%"
          maxWidth="240px"
          margin="0 0 100 0"
        />
        <Skeleton
          height="15px"
          width="100%"
          maxWidth="200px"
          margin="0 0 300 0"
        />
        <Skeleton height="16px" width="100%" maxWidth="240px" />
      </Box>
    );
  }

  if (isEmpty) {
    return (
      <Text.Paragraph subdued>
        Please edit your billing information...
      </Text.Paragraph>
    );
  }

  return (
    <>
      {(lineName || lineContact) && (
        <Box margin="0 0 200 0">
          {lineName && (
            <Text.Heading variant="heading-3">{lineName}</Text.Heading>
          )}
          {lineContact && <Text.Paragraph>{lineContact}</Text.Paragraph>}
        </Box>
      )}
      {lineAddress && <Text.Paragraph>{lineAddress}</Text.Paragraph>}
      {lineLocation && <Text.Paragraph>{lineLocation}</Text.Paragraph>}
      {lineCountry && <Text.Paragraph>{lineCountry}</Text.Paragraph>}
      {lineCompanyVatin && (
        <Box padding="200 0 0 0">
          <Text.Paragraph>{lineCompanyVatin}</Text.Paragraph>
        </Box>
      )}
    </>
  );
};
